import {
  selectIsCampaignPerformanceAlertsFeatureFlagOn,
  selectIsJourneysCampaignPerformanceAlertsFeatureFlagOn,
} from 'core/userContext/selectors';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { InfoBanner } from 'ui/banner';
import { BUTTON_SIZES, BUTTON_VARIANTS, Button } from 'ui/button';
import { MODAL_SIZES, MODAL_VARIANTS, Modal, ModalBody, ModalFooter, ModalHeader } from 'ui/modal';
import { BoldText, spacing } from 'ui/theme';
import { TOGGLE_JOURNEY_MODAL } from '../../../constants';
import { closeModal, getWorkflow, updateWorkflowStatusRequest } from '../../../ducks';

type Props = {
  /** Whether the modal is open */
  isOpen: boolean;
};

const StyledInfoBanner = styled(InfoBanner)`
  margin-top: ${spacing.base};
  margin-bottom: ${spacing.half};
`;

export const ToggleJourneyModal = ({ isOpen }: Props) => {
  /* Change Handlers */
  const dispatch = useDispatch();
  const workflow = useSelector(getWorkflow);
  const isCampaignPerformanceAlertsEnabled = useSelector(
    selectIsCampaignPerformanceAlertsFeatureFlagOn,
  );
  const isJourneysCampaignPerformanceAlertsEnabled = useSelector(
    selectIsJourneysCampaignPerformanceAlertsFeatureFlagOn,
  );

  const { enabled: isWorkflowEnabled, id: workflowId } = workflow;
  const isAlertsFeatureEnabled =
    isCampaignPerformanceAlertsEnabled && isJourneysCampaignPerformanceAlertsEnabled;
  const shouldCancelAlerts = isAlertsFeatureEnabled && !!isWorkflowEnabled;
  const shouldRescheduleAlerts = isAlertsFeatureEnabled && !isWorkflowEnabled;

  const handleCloseModal = () => dispatch(closeModal(TOGGLE_JOURNEY_MODAL));

  const toggleWorkflowStatus = () => {
    // flip the enabledness status
    const newWorkflowStatus = !isWorkflowEnabled;

    // we have a specific endpoint for enabling a workflow
    dispatch(
      updateWorkflowStatusRequest(
        [Number(workflowId)],
        newWorkflowStatus,
        shouldCancelAlerts,
        shouldRescheduleAlerts,
      ),
    );

    // close the modal
    handleCloseModal();
  };

  return (
    <Modal
      id={TOGGLE_JOURNEY_MODAL}
      isModalOpen={isOpen}
      onClose={handleCloseModal}
      size={MODAL_SIZES.SMALL}
      variant={MODAL_VARIANTS.WARNING}
    >
      <ModalHeader>{!isWorkflowEnabled ? 'Turn on journey?' : 'Turn off journey?'}</ModalHeader>
      <ModalBody>
        {!isWorkflowEnabled ? (
          <>
            <p>
              Are you sure you want to turn on <BoldText>{workflow.name}</BoldText>?
            </p>
            <p>
              Contacts will be able to enter this journey and start receiving messages from this
              journey&lsquo;s campaigns.
            </p>
          </>
        ) : (
          <>
            <p>
              Are you sure you want to turn off <BoldText>{workflow.name}</BoldText>?
            </p>
            <p>
              This will prevent new users from being added to the journey and remove existing users
              after they complete the step they&lsquo;re on (or within 2 days if they&lsquo;re in a
              long delay).
            </p>
            <StyledInfoBanner>
              To keep existing users moving through the journey while preventing new users from
              being added, disconnect the Start tile instead of turning off the journey.
            </StyledInfoBanner>
          </>
        )}
      </ModalBody>
      <ModalFooter>
        <Button
          autoFocus
          onClick={toggleWorkflowStatus}
          size={BUTTON_SIZES.LARGE}
          variant={!isWorkflowEnabled ? BUTTON_VARIANTS.PRIMARY : BUTTON_VARIANTS.DESTRUCTIVE}
        >
          {!isWorkflowEnabled ? 'Turn on journey' : 'Turn off journey'}
        </Button>
      </ModalFooter>
    </Modal>
  );
};
